// src/context/AuthContext.js
import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import axios from "axios";
// 1. Import your custom hook
import useInactivityTimer from "../hooks/useInactivityTimer"; 

const BEACONSMS_API_BASE = "https://beaconsms.com/oldsite";
const GET_USER_ID_API = `${BEACONSMS_API_BASE}/index.php/api/v1/mini/getuser`;
const LOGIN_API_BASE = `${BEACONSMS_API_BASE}/api/v1/user/login`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("authToken"));
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const isLoggedIn = !!user && !!token;

  // --- 2. Action: Logout ---
  const logoutAction = useCallback((reason = "manual") => {
    console.log(`AuthProvider: Logging out (Reason: ${reason}).`);
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    setUser(null);
    setToken(null);
    setError(null);
    
    // Optional: Force a redirect to login if the app doesn't do it automatically via routes
    if (reason === "inactivity") {
       window.location.href = "/login?reason=inactivity";
    }
  }, []);

  // --- 3. Initialize Inactivity Timer ---
  // This hook will now run. If the user is logged in, it starts the 15m countdown.
  // If they move/click, it resets. If it hits 0, it calls logoutAction("inactivity").
  useInactivityTimer(() => {
    if (isLoggedIn) {
      logoutAction("inactivity");
    }
  }, 15 * 60 * 1000); // 15 Minutes


  // --- Initialization Effect ---
  useEffect(() => {
    const checkStoredAuth = () => {
      setIsLoading(true);
      const storedToken = localStorage.getItem("authToken");
      const storedUserJson = localStorage.getItem("user");

      if (storedToken && storedUserJson) {
        try {
          const storedUser = JSON.parse(storedUserJson);
          if (storedUser?.id && storedUser?.username) {
            setToken(storedToken);
            setUser(storedUser);
          }
        } catch (e) {
          localStorage.removeItem("user");
          localStorage.removeItem("authToken");
        }
      }
      setIsLoading(false);
    };
    checkStoredAuth();
  }, []);

  // --- Action: Login ---
  const loginAction = useCallback(async (username, password) => {
    setIsLoading(true);
    setError(null);

    const loginUrl = `${LOGIN_API_BASE}/${encodeURIComponent(username)}/${encodeURIComponent(password)}`;

    try {
      const loginResponse = await axios.get(loginUrl);
      const receivedToken = loginResponse.data?.session;

      if (!receivedToken) throw new Error("Login failed: Invalid session.");

      localStorage.setItem("authToken", receivedToken);
      setToken(receivedToken);

      const getUserUrl = `${GET_USER_ID_API}?username=${encodeURIComponent(username)}`;
      const userDetailsResponse = await axios.get(getUserUrl);
      const userId = userDetailsResponse.data?.data?.[0]?.id;

      if (!userId) throw new Error("Could not retrieve user details.");

      const loggedInUser = { id: userId, username: username };
      localStorage.setItem("user", JSON.stringify(loggedInUser));
      setUser(loggedInUser);
      setIsLoading(false);
      return true;
    } catch (err) {
      localStorage.removeItem("authToken");
      localStorage.removeItem("user");
      setToken(null);
      setUser(null);
      setError(err.message);
      setIsLoading(false);
      return false;
    }
  }, []);

  const contextValue = useMemo(() => ({
    user,
    token,
    isLoggedIn,
    isLoading,
    error,
    login: loginAction,
    logout: logoutAction,
  }), [user, token, isLoggedIn, isLoading, error, loginAction, logoutAction]);

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};